import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType, BorderStyle } from "docx";
import { saveAs } from "file-saver";

export const generateDocx = async (useCase) => {
  const doc = new Document({
    styles: {
      paragraphStyles: [
        {
          id: "Normal",
          name: "Normal",
          run: {
            font: "Calibri",
            size: 24,
          },
        },
      ],
    },
    sections: [
      {
        properties: {},
        children: [
          // Title
          new Paragraph({
            text: useCase.title,
            heading: HeadingLevel.HEADING_1,
            spacing: { after: 200 },
          }),

          // Metadata line
          new Paragraph({
            children: [
              new TextRun({
                text: `Products: ${useCase.product.join(", ")}`,
                bold: true,
                size: 22,
              }),
              new TextRun({
                text: `  |  Markets: ${useCase.market.join(", ")}`,
                size: 22,
              }),
              new TextRun({
                text: `  |  Industries: ${useCase.industry.join(", ")}`,
                size: 22,
              }),
            ],
            spacing: { after: 400 },
          }),

          // Summary
          new Paragraph({
            text: "Summary",
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 200, after: 100 },
          }),
          new Paragraph({
            text: useCase.summary,
            spacing: { after: 300 },
          }),

          // Pain Points Section
          new Paragraph({
            text: "Pain Points",
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 200, after: 100 },
          }),
          ...useCase.painPoints.map(
            (point) =>
              new Paragraph({
                text: `• ${point}`,
                spacing: { after: 80 },
              })
          ),

          // Solution Section
          new Paragraph({
            text: "Solution",
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 200, after: 100 },
          }),
          new Paragraph({
            text: useCase.solution,
            spacing: { after: 300 },
          }),

          // Value Proposition Section
          new Paragraph({
            text: "Value Proposition",
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 200, after: 100 },
          }),
          ...useCase.valueProposition.map(
            (value) =>
              new Paragraph({
                text: `• ${value}`,
                spacing: { after: 80 },
              })
          ),

          // Technical Context Section (if exists)
          ...(useCase.technicalContext
            ? [
                new Paragraph({
                  text: "Technical Context",
                  heading: HeadingLevel.HEADING_2,
                  spacing: { before: 200, after: 100 },
                }),
                new Paragraph({
                  text: useCase.technicalContext,
                  spacing: { after: 300 },
                }),
              ]
            : []),

          // Divider
          new Paragraph({
            text: "",
            spacing: { before: 400, after: 200 },
            border: {
              bottom: {
                color: "cccccc",
                space: 1,
                size: 6,
                style: BorderStyle.SINGLE,
              },
            },
          }),

          // Attribution
          new Paragraph({
            children: [
              new TextRun({
                text: `This use case was developed for: ${useCase.clientNote}`,
                italics: true,
                size: 22,
              }),
            ],
            spacing: { before: 200, after: 200 },
          }),

          // Footer
          new Paragraph({
            children: [
              new TextRun({
                text: "Prepared by Arcobi · Confidential",
                italics: true,
                size: 20,
                color: "666666",
              }),
            ],
            alignment: AlignmentType.CENTER,
            spacing: { before: 400 },
          }),
        ],
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  const filename = `${useCase.id || "use-case"}-${useCase.title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .substring(0, 50)}.docx`;
  saveAs(blob, filename);
};

export const generateBulkDocx = async (useCases) => {
  const sections = useCases.map((useCase, index) => ({
    properties: {},
    children: [
      // Title
      new Paragraph({
        text: useCase.title,
        heading: HeadingLevel.HEADING_1,
        spacing: { after: 200 },
      }),

      // Metadata line
      new Paragraph({
        children: [
          new TextRun({
            text: `Products: ${useCase.product.join(", ")}`,
            bold: true,
            size: 22,
          }),
          new TextRun({
            text: `  |  Markets: ${useCase.market.join(", ")}`,
            size: 22,
          }),
          new TextRun({
            text: `  |  Industries: ${useCase.industry.join(", ")}`,
            size: 22,
          }),
        ],
        spacing: { after: 400 },
      }),

      // Summary
      new Paragraph({
        text: "Summary",
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 200, after: 100 },
      }),
      new Paragraph({
        text: useCase.summary,
        spacing: { after: 300 },
      }),

      // Pain Points Section
      new Paragraph({
        text: "Pain Points",
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 200, after: 100 },
      }),
      ...useCase.painPoints.map(
        (point) =>
          new Paragraph({
            text: `• ${point}`,
            spacing: { after: 80 },
          })
      ),

      // Solution Section
      new Paragraph({
        text: "Solution",
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 200, after: 100 },
      }),
      new Paragraph({
        text: useCase.solution,
        spacing: { after: 300 },
      }),

      // Value Proposition Section
      new Paragraph({
        text: "Value Proposition",
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 200, after: 100 },
      }),
      ...useCase.valueProposition.map(
        (value) =>
          new Paragraph({
            text: `• ${value}`,
            spacing: { after: 80 },
          })
      ),

      // Technical Context Section (if exists)
      ...(useCase.technicalContext
        ? [
            new Paragraph({
              text: "Technical Context",
              heading: HeadingLevel.HEADING_2,
              spacing: { before: 200, after: 100 },
            }),
            new Paragraph({
              text: useCase.technicalContext,
              spacing: { after: 300 },
            }),
          ]
        : []),

      // Attribution
      new Paragraph({
        children: [
          new TextRun({
            text: `This use case was developed for: ${useCase.clientNote}`,
            italics: true,
            size: 22,
          }),
        ],
        spacing: { before: 200, after: 200 },
      }),

      // Footer
      new Paragraph({
        children: [
          new TextRun({
            text: "Prepared by Arcobi · Confidential",
            italics: true,
            size: 20,
            color: "666666",
          }),
        ],
        alignment: AlignmentType.CENTER,
        spacing: { before: 400 },
      }),

      // Page break between use cases (except for the last one)
      ...(index < useCases.length - 1
        ? [
            new Paragraph({
              pageBreakBefore: true,
            }),
          ]
        : []),
    ],
  }));

  const doc = new Document({
    styles: {
      paragraphStyles: [
        {
          id: "Normal",
          name: "Normal",
          run: {
            font: "Calibri",
            size: 24,
          },
        },
      ],
    },
    sections,
  });

  const blob = await Packer.toBlob(doc);
  const timestamp = new Date().toISOString().split("T")[0];
  saveAs(blob, `arcus-power-use-cases-${timestamp}.docx`);
};
